import { Link } from "react-router-dom";
import { Pill, ArrowRight, MapPin, CalendarCheck, ShieldCheck, Stethoscope, ClipboardList, Store } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";

const HERO = "https://static.prod-images.emergentagent.com/jobs/96249dff-82f1-4b88-809f-a554549d2a34/images/ed4819b921f539653a7de25fee2d4bd6f8e328ef2a226c0ad87799fc63afc9a4.jpeg";
const FEATURE = "https://static.prod-images.emergentagent.com/jobs/96249dff-82f1-4b88-809f-a554549d2a34/images/0cb2ed3da9b2d2b7ffa75fbd905482f6375257c4c4cfccc931afc6343a0aa46c.jpeg";

const ROLES = [
  { icon: Stethoscope, title: "Pharmacists", copy: "Licensed RPhs for verification, counseling and full-day coverage." },
  { icon: ClipboardList, title: "Technicians", copy: "Registered techs for intake, data entry, filling and insurance." },
  { icon: Store, title: "Clerks", copy: "Front-end support for register, stocking and delivery coordination." },
];

const PHARMACY_STEPS = [
  { n: "1", t: "Post a shift", c: "Tell us when and where you need staff." },
  { n: "2", t: "Find available workers", c: "See qualified people who match your requirements." },
  { n: "3", t: "Confirm", c: "Connect and confirm the shift." },
];

const WORKER_STEPS = [
  { n: "1", t: "Set your availability", c: "Tell us where and when you want to work." },
  { n: "2", t: "Discover shifts", c: "See opportunities that match you." },
  { n: "3", t: "Get confirmed", c: "Express interest and confirm shifts." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">
              <Pill className="h-4 w-4" />
            </span>
            <span className="font-display text-lg font-semibold tracking-tight">FindRxStaff</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/login" className="btn-ghost" data-testid="landing-signin">
              Sign in
            </Link>
            <Link to="/register?role=pharmacy" className="btn-primary hidden sm:inline-flex" data-testid="landing-cta-header">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
        <div className="animate-rise">
          <span className="pill border-accentx/30 bg-accentx-soft text-accentx-hover">
            Nationwide coverage marketplace for independent pharmacies
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-none tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Find the right pharmacy staff before you need them.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
            Connect independent pharmacies anywhere in the United States with available pharmacists,
            technicians, and clerks for upcoming shifts — without Facebook groups, group texts, or
            last-minute phone calls.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/register?role=pharmacy" className="btn-primary text-base" data-testid="cta-need-staff">
              I need staff <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/register?role=worker" className="btn-accent text-base" data-testid="cta-want-work">
              I want to work <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand" /> ZIP + radius matching</span>
            <span className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-brand" /> Availability first</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /> Credential aware</span>
          </div>
        </div>

        <div className="relative">
          <img src={HERO} alt="Pharmacist behind the counter at Medlife Pharmacy" className="h-[420px] w-full rounded-2xl object-cover shadow-lg" />
          <div className="card-surface absolute -bottom-8 left-4 w-[19rem] p-5 sm:left-auto sm:-right-4">
            <p className="overline">Live shift</p>
            <p className="mt-2 font-display text-lg font-semibold">Pharmacist Coverage</p>
            <p className="text-sm text-slate-500">Ridgewood, Queens, NY</p>
            <div className="mt-3 flex items-baseline justify-between">
              <span className="text-sm text-slate-600">10:00 AM – 5:00 PM</span>
              <span className="font-display text-xl font-semibold text-brand">$75/hr</span>
            </div>
            <span className="pill mt-3 border-emerald-200 bg-emerald-50 text-emerald-700">96% Match</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="overline">Who you can book</p>
        <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          One platform for every role behind the counter.
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {ROLES.map((r) => (
            <div key={r.title} className="card-surface p-7 transition-all hover:-translate-y-1 hover:shadow-md">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft text-brand">
                <r.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold">{r.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <div>
            <p className="overline">For pharmacies</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Post coverage in under a minute.
            </h2>
            <div className="mt-8 space-y-7">
              {PHARMACY_STEPS.map((s) => (
                <div key={s.n} className="flex gap-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand font-display text-sm font-semibold text-white">
                    {s.n}
                  </span>
                  <div>
                    <h4 className="font-display text-lg font-medium">{s.t}</h4>
                    <p className="text-sm text-slate-600">{s.c}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/register?role=pharmacy" className="btn-primary mt-9" data-testid="cta-pharmacy-steps">
              Post your first shift
            </Link>
          </div>
          <div>
            <p className="overline">For workers</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Work where and when you want.
            </h2>
            <div className="mt-8 space-y-7">
              {WORKER_STEPS.map((s) => (
                <div key={s.n} className="flex gap-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accentx font-display text-sm font-semibold text-white">
                    {s.n}
                  </span>
                  <div>
                    <h4 className="font-display text-lg font-medium">{s.t}</h4>
                    <p className="text-sm text-slate-600">{s.c}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/register?role=worker" className="btn-accent mt-9" data-testid="cta-worker-steps">
              Set your availability
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2">
        <img src={FEATURE} alt="Medlife Pharmacy storefront in New York City" className="h-80 w-full rounded-2xl object-cover" />
        <div>
          <p className="overline">Structured, not scattered</p>
          <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            "Coverage tomorrow Ridgewood $85/hr 10-3" becomes a real shift.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Role, date, hours, pay, software, language, and pharmacy environment — captured in structured fields
            with room for free-text notes. Matching is deterministic and fully visible: every worker sees exactly
            why a shift fits, and every pharmacy sees exactly why a worker qualifies.
          </p>
        </div>
      </section>

      <footer>
        <SiteFooter />
      </footer>
    </div>
  );
}
